import { LocalBusinessJsonLd } from 'next-seo';
import type { FC, ReactElement } from 'react';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Configure, InstantSearch, useHits } from 'react-instantsearch';

import { Box } from '@/components/Box';
import { Loader } from '@/components/Loader';
import { getLangSlugForAlgolia } from '@/components/StoresList/utils';
import type { ContentfulPageBoutiqueDetailContentItem } from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import { algoliaSearchClient } from '@/integrations/algolia/client';
import { getIndexName } from '@/integrations/algolia/helpers';
import type { StoreHit } from '@/integrations/algolia/types';
import type { CmsExtraDataRecord } from '@/integrations/contentful';
import { generateSectionKey } from '@/integrations/contentful';
import type { ContentfulData } from '@/integrations/contentful/types';
import { Default } from '@/layouts/Default';
import { convertToOpeningHoursSpecification } from '@/lib/seo/Functions';
import { generateLocalBusinessSchema } from '@/lib/seo/StructuredData';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { renderContentfulBoutiqueDetailSection } from './cms';

export type cmsBoutiqueDetailItemType =
  | ContentfulData
  | ContentfulPageBoutiqueDetailContentItem;
type BoutiqueDetailProps = {
  cmsBoutiqueDetailItems: cmsBoutiqueDetailItemType[];
  cmsExtraData: CmsExtraDataRecord;
  resultId: any;
};
type BoutiqueDetailViewWrapperType = FC<{
  children?: React.ReactNode;
  cmsBoutiqueDetailItems: cmsBoutiqueDetailItemType[];
  cmsExtraData: CmsExtraDataRecord;
  resultId: string;
}> & {
  getLayout?: (page: ReactElement) => ReactElement;
};
export const BoutiqueDetailViewWrapper: BoutiqueDetailViewWrapperType = ({
  cmsBoutiqueDetailItems,
  cmsExtraData,
  resultId,
}) => {
  const locale = useContext(LocaleContext);

  const indexName = getIndexName('STORE', locale);

  return (
    <InstantSearch
      indexName={indexName}
      insights={true}
      searchClient={algoliaSearchClient}
    >
      <Configure facetFilters={[`store_id:${resultId}`]} hitsPerPage={1} />
      <BoutiqueDetailView
        cmsBoutiqueDetailItems={cmsBoutiqueDetailItems}
        cmsExtraData={cmsExtraData}
        resultId={resultId}
      />
    </InstantSearch>
  );
};
export const BoutiqueDetailView = ({
  cmsBoutiqueDetailItems,
  cmsExtraData,
  resultId,
}: BoutiqueDetailProps) => {
  const { hits } = useHits<StoreHit>();
  const { locale: RegionLocale } = useRegionProvider();
  const regionLangSlug = RegionLocale.slug;
  const [loading, setLoading] = useState<boolean>(true);
  const { locale, paths, push, query } = useRouter();
  const index = algoliaSearchClient.initIndex(getIndexName('STORE', locale));
  const [boutiqueHit, setBoutiqueHit] = useState<StoreHit>();

  const structuredData = useMemo(() => {
    return boutiqueHit
      ? generateLocalBusinessSchema(
          boutiqueHit,
          locale,
          process.env.NEXT_PUBLIC_URL,
        )
      : null;
  }, [boutiqueHit]);

  useEffect(() => {
    const resultIdInAlgolia = resultId;
    if (resultIdInAlgolia) {
      index
        .search('', {
          attributesToRetrieve: [
            'city',
            `city_${getLangSlugForAlgolia(regionLangSlug)}`,
            'objectID',
            'name',
            `name_${getLangSlugForAlgolia(regionLangSlug)}`,
            'slug',
            'phone',
            'emails',
            'street',
            `street_${getLangSlugForAlgolia(regionLangSlug)}`,
            'description',
            `description_${getLangSlugForAlgolia(regionLangSlug)}`,
            'fax',
            'service_your_watch',
            'postal_code',
            'country_code',
            'utc_offset',
            'hours',
            'temporary_hours',
            '_geoloc',
            'thumbnail_url',
            'photo_gallery',
            'offers_boutique_pickup',
            'offers_appointment',
          ],
          facetFilters: [`store_id:${resultIdInAlgolia}`],
        })
        .then(({ hits }) => {
          if (hits.length) {
            setLoading(false);
            setBoutiqueHit(hits?.[0] as StoreHit);
          } else {
            setLoading(false);
            push(paths.notFound.url());
          }
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [hits]);
  const isLocalized = query.type === 'googleMap';

  return loading ? (
    <Loader height={400} />
  ) : (
    <>
      <Box>
        <>
          {cmsBoutiqueDetailItems.map(
            (data: cmsBoutiqueDetailItemType, i: number) => (
              <Fragment key={generateSectionKey(data as ContentfulData, i)}>
                {renderContentfulBoutiqueDetailSection(
                  data,
                  isLocalized,
                  boutiqueHit as StoreHit,
                  cmsExtraData,
                )}
              </Fragment>
            ),
          )}
        </>
      </Box>
      {structuredData && (
        <LocalBusinessJsonLd
          address={structuredData.address}
          contactPoint={structuredData.contactPoint}
          description={structuredData.description}
          email={structuredData.email}
          geo={structuredData.geo}
          id={structuredData.url}
          images={[structuredData.image]}
          name={structuredData.name}
          {...(structuredData.openingHours
            ? {
                openingHours: convertToOpeningHoursSpecification(
                  structuredData.openingHours,
                ),
              }
            : {})}
          priceRange={structuredData.priceRange}
          sameAs={structuredData.sameAs}
          telephone={structuredData.telephone}
          type='Store'
          url={structuredData.url}
        />
      )}
    </>
  );
};

BoutiqueDetailViewWrapper.getLayout = (page: ReactElement) => (
  <Default cmsData={page.props.cmsDefaultLayoutData}>{page}</Default>
);
